import * as React from 'react'

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  Trash2Icon,
} from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  IConditionalField,
  IPermitTypeField,
  PermitFieldType,
} from '~/client/graph/types.generated'
import SelectWrapper from '~/client/src/desktop/components/SelectWrapper/SelectWrapper'
import { Button } from '~/client/src/shared/components/Button/Button'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { COLON, EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import RuleValueTag from '../../../../RuleComponents/RuleValueTag'
import { IConditionalSelectOption } from '../types/IConditionalSelectOption'

interface IProps {
  rule?: IConditionalField
  field: IPermitTypeField
  selectedValueCaption?: string
  isCreation?: boolean
  isExpanded?: boolean
  availableSelectOptions?: IConditionalSelectOption[]

  /** When provided, renders in place of the default value display */
  renderValueSelector?: React.ReactNode
  /** Whether picker edit mode is active (pencil → checkmark) */
  isPickerEditMode?: boolean
  /** Whether the edit/save action is disabled (e.g. no selections) */
  isEditDisabled?: boolean

  onToggleRule?: (ruleKey: string) => void
  onRemoveRule: () => void
  /** Toggle edit mode for picker-based rules (location/material resource) */
  onEditToggle?: () => void
  onNewRuleValueSelect?: (event: React.ChangeEvent<HTMLSelectElement>) => void
}

const ICON_SIZE = 18

function ConditionalRuleHeader({
  rule,
  field,
  selectedValueCaption,
  isCreation,
  isExpanded,
  availableSelectOptions,
  renderValueSelector,
  isPickerEditMode,
  isEditDisabled,
  onToggleRule,
  onRemoveRule,
  onEditToggle,
  onNewRuleValueSelect,
}: IProps) {
  const ruleKey = rule?.key || ''
  const ruleFields = rule?.values || []
  const isRuleExpanded = isExpanded || isCreation
  const isChecklist = field.type === PermitFieldType.Checklist
  const fieldCaption = isChecklist
    ? field.type
    : field.caption || field.checklist?.list?.[0]?.text
  const ChevronIcon = isRuleExpanded ? ChevronDownIcon : ChevronRightIcon
  const EditIcon = isPickerEditMode ? CheckIcon : PencilIcon

  return (
    <div
      className={classList({
        'row y-center py5 px16 min-height40': true,
        'bb-palette-brand-lighter': !isCreation && isRuleExpanded,
      })}
    >
      <ChevronIcon
        size={ICON_SIZE}
        className="mr10 text grey-30 pointer"
        onClick={() => onToggleRule(ruleKey)}
      />

      <div className="row y-center text large wrap">
        <div className="my2 mr5 no-grow">{Localization.translator.if}</div>

        <RuleValueTag caption={fieldCaption} field={field} isIconShown />

        <div className="my2 mx5 no-grow nowrap text large lowercase grey-30">
          {isChecklist ? (
            <>
              {Localization.translator.items}{' '}
              {Localization.translator.operatorValues.are}
            </>
          ) : (
            Localization.translator.operatorValues.is
          )}
        </div>

        {isCreation && !renderValueSelector && (
          <SelectWrapper className="no-grow">
            <select
              className="bg-palette-brand-lightest brada4 ba-none pl12 pr24 py6 pointer min-width100 max-width250"
              value={EMPTY_STRING}
              onChange={onNewRuleValueSelect}
            >
              <option value={EMPTY_STRING} className="text large" />
              {availableSelectOptions?.map((opt, index) => (
                <option
                  key={index}
                  value={opt.value}
                  className="text large max-width250"
                >
                  {opt.title}
                </option>
              ))}
            </select>
          </SelectWrapper>
        )}

        {isCreation && renderValueSelector}

        {!isCreation && renderValueSelector}

        {!isCreation && !renderValueSelector && (
          <RuleValueTag caption={selectedValueCaption} field={field} />
        )}

        <div className="my2 mx5 no-grow nowrap text large grey-30">
          {Localization.translator.thenShow}
          {(isRuleExpanded || isCreation) && COLON}
        </div>

        {!isRuleExpanded && !isCreation && (
          <div className="my2 no-grow nowrap">
            {ruleFields.length
              ? Localization.translator.xFields(ruleFields.length)
              : Localization.translator.nothing}
          </div>
        )}
      </div>

      <div className="tw:ml-auto tw:flex tw:flex-none! tw:items-center tw:gap-1">
        {onEditToggle && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={Localization.translator.edit_verb}
            disabled={isEditDisabled}
            onClick={onEditToggle}
          >
            <EditIcon />
          </Button>
        )}
        <Button
          variant="destructive-ghost"
          size="icon-sm"
          aria-label={Localization.translator.delete}
          onClick={onRemoveRule}
        >
          <Trash2Icon />
        </Button>
      </div>
    </div>
  )
}

export default observer(ConditionalRuleHeader)
